import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CircleDollarSignIcon, TagIcon } from 'lucide-react';
import { memo } from 'react';
import { EmployeeWeeklyRecord } from '../types';
import EmployeeRecordsTable from './EmployeeRecordsTable';

interface EmployeeCardProps {
  record: EmployeeWeeklyRecord;
}

const EmployeeCard = memo(({ record }: EmployeeCardProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-4 py-2 cursor-pointer hover:bg-gray-200/60 transition-colors duration-200 rounded-lg px-2">
          {/* Employee Info & Income */}
          <div className="flex-1">
            <div className="flex flex-col items-baseline">
              <div>
                <h3 className="text-lg font-semibold">{record.name}</h3>
              </div>
              <div className="flex items-center gap-1 text-sm text-green-600">
                <CircleDollarSignIcon size={16} /> {record.income}
              </div>
              <div className="text-xs text-gray-500">Supply: {record.supplies}</div>
            </div>
          </div>

          {/* Tasks Progress */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <TagIcon />
              <span className="text-xl font-semibold">
                {record.salary.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </div>
            <Progress value={record.percentage} className="h-2" />
            <div className="text-sm text-gray-500 mt-1">{record.percentage.toFixed(2)}%</div>
          </div>

          {/* Check Details */}
          <div className="min-w-[100px] flex-1 flex justify-end items-center gap-2">
            <div className="flex flex-col text-right">
              <div className="text-blue-600 font-medium">check: {record.check.amount}</div>
              <div className="text-sm text-gray-500">
                tip: {record.check.tip} - cash: {record.check.cash}
              </div>
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content className="w-[370px] z-50 border border-gray-200 bg-white rounded-md shadow-md">
          <EmployeeRecordsTable
            employeeName={record.name}
            records={
              record.tasks?.map(task => ({
                date: task.checkinTime,
                income: task.income,
                supply: task.supplies,
                turn: task.credits,
              })) || []
            }
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  );
});

EmployeeCard.displayName = 'EmployeeCard';

export default EmployeeCard;
